<script setup>
// core
import {computed} from "vue";
import {useRouter} from "vue-router";

// components
import HeaderMain from "@/components/Recurring/HeaderMain.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";

// assets
import tiktokSvg from "@/assets/icons/messengers/tiktok_btn.svg";

// the reasons the tiktok callback can return; anything else falls back to the
// generic one
const KNOWN_REASONS = [
    "business_messaging_unavailable",
    "messaging_permissions_missing",
    "state_used",
    "activation_failed",
];

const props = defineProps({
    status: {
        type: String, // 'success' || 'failed'
        required: true,
    },
    username: {
        type: String,
        default: "",
    },
    reason: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["retry"]);

const router = useRouter();

const isSuccess = computed(() => props.status === "success");

const reasonKey = computed(() =>
    KNOWN_REASONS.includes(props.reason) ? props.reason : "activation_failed"
);

const goToAccounts = () => router.push({name: "Integration"});
</script>

<template>
    <div class="activation">
        <HeaderMain>{{ $t("page.tiktok_activation.mainheader") }}</HeaderMain>

        <div class="activation__block">
            <div class="activation__logo">
                <img :src="tiktokSvg" alt="Tiktok"/>
            </div>

            <div class="activation__start">
                <p class="activation__status" :class="isSuccess ? 'is-success' : 'is-failed'">
                    <i :class="['fa', isSuccess ? 'fa-check-circle-o' : 'fa-info-circle']"/>
                    {{
                        isSuccess
                            ? $t("page.tiktok_activation.result.success_title")
                            : $t("page.tiktok_activation.result.failed_title")
                    }}
                </p>

                <i18n-t
                    v-if="isSuccess"
                    class="activation__text"
                    keypath="page.tiktok_activation.result.success_text"
                    tag="p"
                >
                    <template #username>
                        <b class="activation__username">{{ username }}</b>
                    </template>
                </i18n-t>

                <p v-else class="activation__text">
                    {{ $t(`page.tiktok_activation.result.reasons.${reasonKey}`) }}
                </p>

                <ButtonLoading
                    v-if="isSuccess"
                    class="ec_button"
                    @click="goToAccounts"
                >
                    {{ $t("page.tiktok_activation.result.to_accounts") }}
                </ButtonLoading>
                <ButtonLoading v-else class="ec_button" @click="emit('retry')">
                    {{ $t("page.tiktok_activation.result.retry") }}
                </ButtonLoading>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.activation {
    &__start {
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    &__block {
        margin-top: 30px;
        padding: 23px 48px;

        width: 100%;
        border-radius: 11px;
        background: var(--block-color);
        display: flex;
        align-items: center;
        gap: 52px;

        box-sizing: border-box;
    }

    &__logo {
        flex: 0 0 auto;
        width: 140px;
        height: 140px;
        border-radius: 24px;
        background: var(--tiktok-color);
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 130px;
            height: 130px;
        }
    }

    &__status {
        display: flex;
        align-items: center;
        gap: 12px;

        font-family: 'Gilroy', sans-serif;
        font-size: 24px;
        font-weight: 700;
        line-height: 140%;
        text-transform: none;

        &.is-success {
            color: var(--success-color);
        }

        &.is-failed {
            color: var(--danger-second-color);
        }

        i {
            font-size: 28px;
        }
    }

    &__username {
        font-weight: 700;
        color: var(--text-color);
        letter-spacing: 0.05rem;
    }

    &__text {
        color: var(--text-third-color);
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 170%;
        padding: 0 20px 0 0;
        text-transform: none;

        b {
            font-weight: 700;
            color: var(--text-color);
        }
    }

    @media screen and (max-width: 680px) {
        .activation__block {
            flex-direction: column;
            padding: 15px;
        }

        .activation__status {
            font-size: 18px;

            i {
                font-size: 22px;
            }
        }

        .activation__text {
            font-size: 14px;
            line-height: 150%;
        }

        .activation__start {
            padding: 0;

            .ec_button {
                width: 100%;
            }
        }
    }
}
</style>
